import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Upload, Trash2, Loader2, Image as ImageIcon, Palette, Phone, GripVertical } from "lucide-react";

type CmsSettingRow = { key: string; value: unknown };

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const COLOR_VARS = [
  { key: "--primary", label: "Primary" },
  { key: "--primary-foreground", label: "Primary Foreground" },
  { key: "--accent", label: "Accent" },
  { key: "--accent-foreground", label: "Accent Foreground" },
  { key: "--background", label: "Background" },
  { key: "--foreground", label: "Foreground" },
  { key: "--secondary", label: "Secondary" },
  { key: "--muted", label: "Muted" },
  { key: "--card", label: "Card" },
  { key: "--border", label: "Border" },
];

const IMAGE_SLOTS = [
  { slot: "hero", label: "Hero Background" },
  { slot: "logo", label: "Logo" },
  { slot: "story", label: "Our Story Image" },
  { slot: "og", label: "Social Share Image (OG)" },
  { slot: "service_1", label: "Service 1" },
  { slot: "service_2", label: "Service 2" },
  { slot: "service_3", label: "Service 3" },
  { slot: "service_4", label: "Service 4" },
  { slot: "service_5", label: "Service 5" },
  { slot: "service_6", label: "Service 6" },
];

function hslToHex(hsl: string): string {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return "#888888";
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(l * 100)}%`;
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function validateImageFile(file: File): string | null {
  if (!file.type.startsWith("image/")) {
    return "Only image files are allowed";
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`;
  }
  return null;
}

function ColorPreviewCard({ colors }: { colors: Record<string, string> }) {
  const bg = colors["--background"] ? hslToHex(colors["--background"]) : "#ffffff";
  const fg = colors["--foreground"] ? hslToHex(colors["--foreground"]) : "#111111";
  const primary = colors["--primary"] ? hslToHex(colors["--primary"]) : "#6366f1";
  const primaryFg = colors["--primary-foreground"] ? hslToHex(colors["--primary-foreground"]) : "#ffffff";
  const accent = colors["--accent"] ? hslToHex(colors["--accent"]) : "#f3f4f6";
  const accentFg = colors["--accent-foreground"] ? hslToHex(colors["--accent-foreground"]) : "#111111";
  const secondary = colors["--secondary"] ? hslToHex(colors["--secondary"]) : "#f1f5f9";
  const muted = colors["--muted"] ? hslToHex(colors["--muted"]) : "#f1f5f9";
  const card = colors["--card"] ? hslToHex(colors["--card"]) : "#ffffff";
  const border = colors["--border"] ? hslToHex(colors["--border"]) : "#e2e8f0";

  return (
    <div className="rounded-xl border shadow-sm overflow-hidden" style={{ background: bg, borderColor: border }}>
      <div className="p-3 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50/50">
        Live Preview
      </div>
      <div className="p-4 space-y-3">
        {/* Nav bar */}
        <div className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: card, border: `1px solid ${border}` }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ background: primary }} />
            <span className="text-xs font-semibold" style={{ color: fg }}>Vita Dom</span>
          </div>
          <div className="flex gap-2">
            <span className="text-[10px]" style={{ color: muted !== bg ? accentFg : fg }}>Home</span>
            <span className="text-[10px]" style={{ color: muted !== bg ? accentFg : fg }}>About</span>
            <span className="text-[10px]" style={{ color: muted !== bg ? accentFg : fg }}>Contact</span>
          </div>
        </div>

        {/* Hero section */}
        <div className="rounded-lg px-4 py-5 text-center" style={{ background: primary }}>
          <p className="text-xs font-bold mb-1" style={{ color: primaryFg }}>Welcome to Vita Dom</p>
          <p className="text-[10px] mb-2 opacity-80" style={{ color: primaryFg }}>Quality elder care services</p>
          <span className="inline-block text-[10px] px-2 py-0.5 rounded" style={{ background: accent, color: accentFg }}>
            Learn More
          </span>
        </div>

        {/* Cards row */}
        <div className="grid grid-cols-3 gap-2">
          {["Service 1", "Service 2", "Service 3"].map((s) => (
            <div key={s} className="rounded-md p-2 text-center" style={{ background: card, border: `1px solid ${border}` }}>
              <div className="w-4 h-4 rounded mx-auto mb-1" style={{ background: secondary }} />
              <span className="text-[9px] font-medium" style={{ color: fg }}>{s}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="rounded-md px-3 py-2 flex items-center justify-between" style={{ background: muted }}>
          <span className="text-[9px]" style={{ color: accentFg }}>© Vita Dom</span>
          <span className="text-[9px]" style={{ color: accentFg }}>info@vitadom.com</span>
        </div>
      </div>
    </div>
  );
}

export default function CmsEditor() {
  const [colors, setColors] = useState<Record<string, string>>({});
  const [images, setImages] = useState<Record<string, string>>({});
  const [contact, setContact] = useState({ email: "", phone: "", hours: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadSlot, setUploadSlot] = useState<string | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: settings } = await supabase.from("cms_settings").select("*");
      const { data: imgs } = await supabase.from("cms_images").select("*");

      const settingsMap: Record<string, unknown> = {};
      (settings || []).forEach((r: CmsSettingRow) => { settingsMap[r.key] = r.value; });

      if (settingsMap.colors) setColors(settingsMap.colors as Record<string, string>);
      if (settingsMap.contact) setContact(settingsMap.contact as typeof contact);

      const imgMap: Record<string, string> = {};
      (imgs || []).forEach((r: { slot: string; url: string }) => { imgMap[r.slot] = r.url; });
      setImages(imgMap);

      setLoading(false);
    })();
  }, []);

  const saveColors = async () => {
    setSaving(true);
    const { error } = await supabase.from("cms_settings").upsert({ key: "colors", value: colors });
    setSaving(false);
    if (error) {
      toast.error("Failed to save colors");
    } else {
      toast.success("Colors saved");
    }
  };

  const saveContact = async () => {
    setSaving(true);
    const { error } = await supabase.from("cms_settings").upsert({ key: "contact", value: contact });
    setSaving(false);
    if (error) {
      toast.error("Failed to save contact info");
    } else {
      toast.success("Contact info saved");
    }
  };

  const handleImageUpload = useCallback(async (slot: string, file: File) => {
    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setUploadSlot(slot);
    const path = `${slot}/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("cms").upload(path, file, { upsert: true });
    if (error) {
      toast.error("Upload failed: " + error.message);
      setUploadSlot(null);
      return;
    }
    const { data: urlData } = supabase.storage.from("cms").getPublicUrl(path);
    const url = urlData.publicUrl;

    const { error: dbError } = await supabase.from("cms_images").upsert({ slot, url }, { onConflict: "slot" });
    if (dbError) {
      toast.error("Failed to save image reference");
    } else {
      toast.success("Image uploaded");
    }
    setImages((prev) => ({ ...prev, [slot]: url }));
    setUploadSlot(null);
  }, []);

  const removeImage = async (slot: string) => {
    const { error } = await supabase.from("cms_images").delete().eq("slot", slot);
    if (error) {
      toast.error("Failed to remove image");
    } else {
      setImages((prev) => { const n = { ...prev }; delete n[slot]; return n; });
      toast.success("Image removed");
    }
  };

  const handleDragOver = (e: React.DragEvent, slot: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(slot);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(null);
  };

  const handleDrop = (e: React.DragEvent, slot: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(null);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageUpload(slot, file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">CMS Settings</h2>

      <Tabs defaultValue="colors">
        <TabsList>
          <TabsTrigger value="colors" className="gap-1.5">
            <Palette className="w-4 h-4" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="images" className="gap-1.5">
            <ImageIcon className="w-4 h-4" />
            Images
          </TabsTrigger>
          <TabsTrigger value="contact" className="gap-1.5">
            <Phone className="w-4 h-4" />
            Contact
          </TabsTrigger>
        </TabsList>

        <TabsContent value="colors" className="mt-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {COLOR_VARS.map(({ key, label }) => {
                  const hslVal = colors[key] || "";
                  const hexVal = hslVal ? hslToHex(hslVal) : "#888888";
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <input
                        type="color"
                        value={hexVal}
                        onChange={(e) => setColors((prev) => ({ ...prev, [key]: hexToHsl(e.target.value) }))}
                        className="w-10 h-10 rounded-lg border cursor-pointer"
                      />
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-700">{label}</label>
                        <p className="text-xs text-gray-400 font-mono">{key}: {hslVal || "default"}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button onClick={saveColors} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Colors
              </Button>
            </div>

            <div className="hidden lg:block">
              <ColorPreviewCard colors={colors} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="images" className="mt-6">
          <div className="space-y-4">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && uploadSlot) handleImageUpload(uploadSlot, f);
                e.target.value = "";
              }}
            />
            {IMAGE_SLOTS.map(({ slot, label }) => (
              <div key={slot} className="bg-white rounded-xl border shadow-sm p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{label}</h3>
                  {images[slot] && (
                    <button onClick={() => removeImage(slot)} className="text-red-400 hover:text-red-600 flex items-center gap-1 text-sm">
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
                {images[slot] ? (
                  <div
                    onDragOver={(e) => handleDragOver(e, slot)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, slot)}
                    className="relative"
                  >
                    <img src={images[slot]} alt={label} className="rounded-lg max-h-48 object-cover border" />
                    {dragOverSlot === slot && (
                      <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">Drop to replace</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    onClick={() => { setUploadSlot(slot); fileRef.current?.click(); }}
                    onDragOver={(e) => handleDragOver(e, slot)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, slot)}
                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 w-full transition-colors cursor-pointer ${
                      dragOverSlot === slot
                        ? "border-primary bg-primary/10"
                        : "border-gray-300 hover:border-primary/50 hover:bg-primary/5"
                    }`}
                  >
                    {uploadSlot === slot ? (
                      <Loader2 className="w-8 h-8 text-gray-400 mb-2 animate-spin" />
                    ) : dragOverSlot === slot ? (
                      <Upload className="w-8 h-8 text-primary mb-2" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                    )}
                    <span className="text-sm text-gray-500">
                      {dragOverSlot === slot ? "Drop image here" : "Click or drag & drop to upload"}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">Max 5MB, images only</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contact" className="mt-6">
          <div className="bg-white rounded-xl border shadow-sm p-6 max-w-xl">
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Working Hours</label>
                <input
                  type="text"
                  value={contact.hours}
                  onChange={(e) => setContact((p) => ({ ...p, hours: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                />
              </div>
            </div>
            <Button onClick={saveContact} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Contact
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
